import { faCheck, faHand, faRobot, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LinearProgress } from '@material-ui/core';
import { orderBy } from 'lodash';
import React from 'react';
import { QueuedItemStatus } from '../../../../../../graphql/projects/models/QueuedItemStatus';
import VoxelIconButton from '../../../../../voxel/Buttons/VoxelIconButton';
import VoxelChip from '../../../../../voxel/Chips/VoxelChip';
import VoxelInfo from '../../../../../voxel/Info/VoxelInfo';
import { usePopoverContext } from '../../../../../voxel/Popover/PopoverLogic';
import VoxelText from '../../../../../voxel/Text/VoxelText';
import VoxelTooltipShort from '../../../../../voxel/Tooltip/VoxelTooltipShort';
import { TableCellKeywordProps } from './TableCellKeyword';

export interface VariantPopoverContentProps extends TableCellKeywordProps {}

export const VariantPopoverContent: React.FC<VariantPopoverContentProps> = ({
  keyword,
  areVariantsLoading,
  localVariant,
  handleSelectVariant,
}) => {
  const localKeyword = localVariant
    ? keyword.keywordVariants.find((v) => v.id === localVariant)?.variant ?? keyword.keyword
    : keyword.keyword;

  const hasManualSelection =
    !!localVariant || !!keyword.keywordVariants?.some((kv) => kv.selectedManually);

  const popoverContext = usePopoverContext();

  return (
    <div className="w-72 relative">
      {/* Close button */}
      {/* We're doing an "invisiable" padded area around to increase the click surface */}
      <div
        className="absolute -right-0.5 -top-0.5 p-2 group cursor-pointer"
        onClick={() => popoverContext.setOpen(false)}
      >
        <VoxelIconButton
          size="xs"
          color="white"
          icon={<FontAwesomeIcon title={'Close'} icon={faXmark} />}
        />
      </div>
      <div
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="p-3 pb-2 rounded-t-md flex flex-row items-center gap-2">
          <VoxelText variant="bread-medium" color="medium" className="!text-xs">
            Similar keyword variants
          </VoxelText>
          <VoxelInfo
            tooltip={`Google Ads only provides combined data for these words, so they're treated as a single keyword in Voxel.`}
          />
          {keyword.variantStatus === QueuedItemStatus.QUEUED && (
            <VoxelChip
              title="Queued"
              tooltip={`Queued for evaluation. Algorithm will identify the best variant (beta feature)`}
              color="warning"
            />
          )}
          {keyword.variantStatus === QueuedItemStatus.PROCESSING && (
            <VoxelChip
              title="Processing"
              tooltip={`Algorithm is identifying the best variant (beta feature)`}
              color="warning"
            />
          )}
        </div>

        {/* Loading bar */}
        {areVariantsLoading && (
          <div className="absolute w-full z-10 opacity-20">
            <LinearProgress />
          </div>
        )}

        {/* Variants list */}
        <div className="divide-solid divide-y divide-voxel-gray-200">
          {orderBy(keyword.keywordVariants, (kv) => kv.variant)?.map((variant) => (
            <div
              key={variant.id}
              className={`
                  px-3 py-2.5 rounded
                  hover:bg-gray-100/75
                  ${!areVariantsLoading && 'cursor-pointer'}
                  text-[13px] text-gray-800
                  flex items-center gap-2
                `}
              onClick={() => {
                !areVariantsLoading && handleSelectVariant(keyword.id, variant.id);
              }}
            >
              {/* The variant */}
              {variant.variant}
              <span className="flex flex-grow" />

              {/* Default selection */}
              {variant.variant === localKeyword &&
                !variant.selectedByAlgorithm &&
                !hasManualSelection && (
                  <VoxelTooltipShort message="Selected">
                    <div className="bg-success-600/70 text-white px-1 rounded">
                      <FontAwesomeIcon icon={faCheck} size="sm" className="text-white" />
                    </div>
                  </VoxelTooltipShort>
                )}

              {/* Manual selection */}
              {(!!localVariant ? variant.variant === localKeyword : variant.selectedManually) && (
                <VoxelTooltipShort message="Manually selected (overrides algorithm)">
                  <div className="bg-success-600/70 text-white px-1 rounded">
                    <FontAwesomeIcon icon={faHand} size="sm" className="text-white" />
                  </div>
                </VoxelTooltipShort>
              )}

              {/* Algorithm selection */}
              {variant.selectedByAlgorithm && (
                <VoxelTooltipShort message="Preferred by algorithm">
                  <div
                    className={`${
                      hasManualSelection ? 'bg-voxel-gray-500' : 'bg-success-600/70'
                    } text-white px-1 rounded`}
                  >
                    <FontAwesomeIcon icon={faRobot} size="sm" className="text-white" />
                  </div>
                </VoxelTooltipShort>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VariantPopoverContent;
